<template>
  <lp-select
    v-model="selected"
    :options="options"
    track-by="value"
    label-by="name"
    :label-top="labelTop"
    :placeholder="placeholder"
    :status="status"
    :message="message"
    :required="required"
    :preview="preview"
    :data-ua-unique-id="dataUaUniqueId"
    v-bind="$attrs"
    @update:modelValue="handleSelected"
  />
</template>

<script setup lang="ts">
import {
  subMinutes,
  addMinutes,
  format as dateFormat,
  roundToNearestMinutes,
  parseISO,
} from 'date-fns'
import { computed, ref, watchEffect } from 'vue'
import { HALF_HOUR } from '@/components/timepicker/constants'
import { t } from '@/plugins/i18n'

defineOptions({
  name: 'lp-time-picker',
  inheritAttrs: false,
})

const emit = defineEmits(['update:modelValue'])

interface Props {
  modelValue: Date | string | null
  default?: Date | string | null
  interval?: number | string
  labelTop?: string
  placeholder?: string
  format?: string
  status?: string
  message?: string | null
  required?: boolean
  preview?: boolean
  dataUaUniqueId?: string
}

const props = withDefaults(defineProps<Props>(), {
  interval: 5,
  modelValue: null,
  labelTop: t('LpTimePicker.labelTop'),
  placeholder: t('LpTimePicker.placeholder'),
  format: 'hh:mm a',
  status: 'neutral',
  message: null,
  required: false,
  default: null,
  preview: false,
  dataUaUniqueId: 'ua-timepicker',
})

const slotConfig = {
  configSlotMinutes: props.interval,
  timeArr: [{ startTime: '00:00', endTime: '24:00' }],
}
const options = computed(() => {
  // Getting values from slotConfig using destructuring
  const { configSlotMinutes, timeArr } = slotConfig

  // This is the default date that we are using to make use of javascript date functions
  // slotsArray will hold final slots
  // _timeArrStartTime is used to store start time date object from the timeArr
  // _timeArrEndTime is used to store end time date object from the timeArr
  // _tempSlotStartTime is used to create slots by adding config values and check that the time is less than the end time and lies withing the duration specified
  // _startSlot holds value of start date time of slot
  // _endSlot holds value of end date time of slot

  const defaultDate = new Date().toLocaleDateString('en-US').replace(/\//g, '-')
  const slotsArray = [{ value: null, name: 'Select a time' }]
  let _timeArrStartTime
  let _timeArrEndTime
  let _tempSlotStartTime
  let _endSlot
  let _startSlot

  // Loop over timeArr
  for (let i = 0; i < timeArr.length; i++) {
    // Creating time stamp using time from timeArr and default date
    _timeArrStartTime = new Date(defaultDate + ' ' + timeArr[i].startTime)
    _timeArrEndTime = new Date(defaultDate + ' ' + timeArr[i].endTime)
    _tempSlotStartTime = _timeArrStartTime

    // Loop around till _tempSlotStartTime is less end time from timeArr
    while (
      new Date(_tempSlotStartTime) <
      new Date(subMinutes(_timeArrEndTime, parseInt(configSlotMinutes)))
    ) {
      _endSlot = new Date(_tempSlotStartTime)
      _startSlot = new Date(_tempSlotStartTime)

      // _tempSlotStartTime = addMinutes(_endSlot, parseInt(configSlotMinutes))

      _tempSlotStartTime =
        _tempSlotStartTime !== _timeArrStartTime
          ? addMinutes(_endSlot, parseInt(configSlotMinutes))
          : _endSlot

      // Check _tempSlotStartTime is less than end time after adding minutes and hours, if true push into slotsArr
      if (new Date(_tempSlotStartTime) <= new Date(_timeArrEndTime)) {
        // DateTime object is converted to time with the help of javascript functions
        // If you want 24-hour format you can pass hour12 false
        slotsArray.push({
          value: new Date(_tempSlotStartTime).toISOString(),
          name: dateFormat(_tempSlotStartTime, props.format),
        })
      }
    }
  }

  return slotsArray
})
const roundTime = (time: string | number | Date | null) => {
  if (time === null) return null
  if (time instanceof Date) {
    if (isNaN(time.getTime())) {
      return null
    }
  }

  return dateFormat(
    roundToNearestMinutes(time, {
      nearestTo: Number(
        props.interval > HALF_HOUR ? HALF_HOUR : props.interval,
      ),
    }),
    props.format,
  )
}

const selected = ref<string | null>(null)

try {
  selected.value = roundTime(props.modelValue)
} catch (error) {
  throw new Error(
    JSON.stringify({
      error: { message: error.message },
      props,
    }),
  )
}

const handleSelected = () => {
  emit(
    'update:modelValue',
    selected.value === null ? null : parseISO(selected.value),
  )
}

watchEffect(() => {
  selected.value = roundTime(props.modelValue)
})
</script>
